// NOTE: This file is mirrored byte-identical at
// selfservice-api/src/shared/creative-coverage.ts. Keep the two in sync.
// See ADR-0006.

export interface CoverageCreative {
  startDate: string;
  endDate: string;
}

export interface UncoveredRange {
  startDate: string;
  endDate: string;
}

export interface CoverageResult {
  covered: boolean;
  uncoveredRanges: UncoveredRange[];
}

export function validateCreativeCoverage(args: {
  windowStart: string;
  windowEnd: string;
  creatives: CoverageCreative[];
}): CoverageResult {
  const { windowStart, windowEnd, creatives } = args;
  const windowStartT = parseDate(windowStart);
  const windowEndT = parseDate(windowEnd);
  if (windowStartT == null || windowEndT == null || windowEndT < windowStartT) {
    return {
      covered: false,
      uncoveredRanges: [{ startDate: windowStart, endDate: windowEnd }],
    };
  }

  const intervals: [number, number][] = [];
  for (const c of creatives) {
    const s = parseDate(c.startDate);
    const e = parseDate(c.endDate);
    if (s == null || e == null || e < s) continue;
    const lo = Math.max(s, windowStartT);
    const hi = Math.min(e, windowEndT);
    if (hi < lo) continue;
    intervals.push([lo, hi]);
  }
  intervals.sort((a, b) => a[0] - b[0]);

  const gaps: UncoveredRange[] = [];
  let cursor = windowStartT;
  for (const [s, e] of intervals) {
    if (s > cursor) {
      gaps.push({ startDate: formatDate(cursor), endDate: formatDate(s - DAY_MS) });
    }
    if (e + DAY_MS > cursor) cursor = e + DAY_MS;
    if (cursor > windowEndT) break;
  }
  if (cursor <= windowEndT) {
    gaps.push({ startDate: formatDate(cursor), endDate: formatDate(windowEndT) });
  }
  return { covered: gaps.length === 0, uncoveredRanges: gaps };
}

const DAY_MS = 86_400_000;

function parseDate(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).slice(0, 10));
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const t = Date.UTC(y, mo - 1, d);
  const back = new Date(t);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) {
    return null;
  }
  return t;
}

function formatDate(t: number): string {
  const d = new Date(t);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}
